import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef } from "react";

import { TableHeaderField } from "@/lib/interfaces and Enums/ENUMTableHeaders";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { bulkSaveUnwindBlockValuesToDatabase } from "@/services/ActionSummaryTableSERVICES";
import tradeBlockDataStore from "@/store/tradeBlockDataStore";
import tradeDataStore from "@/store/tradeDataStore";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import {
  getBlocksCheckboxState,
  getBlocksForTradeFromStore,
  getHeaderCheckboxState,
} from "./UnwindHeaderCheckboxHelpers";

interface UnwindHeaderCheckboxProps {
  fullTradeData: ExecutionStrategy;
  fieldType: "npv" | "recoupon";
}

const UnwindHeaderCheckbox = (params: UnwindHeaderCheckboxProps) => {
  const { getAccessTokenSilently } = useAuth0();
  const checkboxRef = useRef<HTMLInputElement>(null);

  const fullTradeData = params.fullTradeData;
  const isNPV = params.fieldType === "npv";
  const headerText = isNPV ? TableHeaderField.npv : TableHeaderField.recoupon;

  const currentTradeBlockData = tradeBlockDataStore(
    (state) => state.tradeBlockData,
  );
  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const allTradeSettings = tradeSettingsStore(
    (state) => state.tradeSettingsData,
  );

  const thisTrade = currentTradeData.find(
    (trade) => trade.esid === fullTradeData.esid,
  );
  const thisTradeSettings = allTradeSettings.find(
    (t) => t.esid === thisTrade?.esid,
  )?.settings;

  const blocks = getBlocksForTradeFromStore(
    currentTradeBlockData,
    fullTradeData.esid,
  );
  const blockStates = getBlocksCheckboxState(
    blocks,
    thisTrade,
    thisTradeSettings,
    isNPV,
  );
  const { isChecked, isIndeterminate, isDisabled, enabledBlocks } =
    getHeaderCheckboxState(blockStates);

  // Handle indeterminate state via ref (can't set via attribute)
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCheckedValue = event.target.checked;

    // Get block IDs that need to change
    const blockIdsToUpdate = enabledBlocks
      .filter((blockState) => blockState.isChecked !== newCheckedValue)
      .map((blockState) => blockState.block.id);

    if (blockIdsToUpdate.length > 0) {
      await bulkSaveUnwindBlockValuesToDatabase(
        fullTradeData.esid,
        blockIdsToUpdate,
        getAccessTokenSilently,
        params.fieldType,
        newCheckedValue,
      );
    }
  };

  // Don't show checkbox if no SWP blocks
  if (blockStates.length === 0) {
    return (
      <div data-testid={`unwindHeader-${params.fieldType}`}>{headerText}</div>
    );
  }

  return (
    <div
      data-testid={`unwindHeader-${params.fieldType}`}
      className="flex items-center gap-1"
    >
      <span>{headerText}</span>
      <div className="ag-labeled ag-label-align-right ag-checkbox ag-input-field">
        <div
          className={`ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper ${isChecked ? "ag-checked" : ""} ${isDisabled ? "ag-disabled" : ""}`}
        >
          <input
            ref={checkboxRef}
            type="checkbox"
            className="ag-cell-wrapper ag-checkbox-cell"
            checked={isChecked}
            onChange={handleChange}
            disabled={isDisabled}
            data-testid={`unwindHeaderCheckbox-${params.fieldType}`}
          />
        </div>
      </div>
    </div>
  );
};

export default UnwindHeaderCheckbox;
